import { useEffect } from "react";
import { useI18n } from "../i18n";
import { SITE_URL, LANGS, pathFor, getLang, ANALYTICS } from "../config/site";
import { IMAGES } from "../data/structure";

const OG_IMAGE = IMAGES.heroGold;

function setHtmlLang(lang) {
  const meta = getLang(lang);
  document.documentElement.setAttribute("lang", lang);
  document.documentElement.setAttribute("dir", meta.dir);
}

function clearManaged() {
  document.head.querySelectorAll("[data-seo='1']").forEach((el) => el.remove());
}

function tag(name, attrs) {
  const el = document.createElement(name);
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  el.setAttribute("data-seo", "1");
  return el;
}

/**
 * Manages document head: title, meta, canonical, hreflang, OpenGraph, Twitter,
 * JSON-LD. Lightweight replacement for react-helmet (no extra dependency).
 */
export function useSeo({ pageKey, title, description, jsonLd = [], noindex = false }) {
  const { lang } = useI18n();

  useEffect(() => {
    setHtmlLang(lang);
    document.title = title;
    clearManaged();

    const frag = document.createDocumentFragment();
    const canonical = SITE_URL + pathFor(lang, pageKey);

    frag.appendChild(tag("meta", { name: "description", content: description }));
    frag.appendChild(tag("meta", { name: "robots", content: noindex ? "noindex,nofollow" : "index,follow" }));
    frag.appendChild(tag("link", { rel: "canonical", href: canonical }));

    if (ANALYTICS.googleSiteVerification) {
      frag.appendChild(tag("meta", { name: "google-site-verification", content: ANALYTICS.googleSiteVerification }));
    }

    // hreflang alternates
    LANGS.forEach((l) => {
      frag.appendChild(tag("link", { rel: "alternate", hreflang: l.code, href: SITE_URL + pathFor(l.code, pageKey) }));
    });
    frag.appendChild(tag("link", { rel: "alternate", hreflang: "x-default", href: SITE_URL + pathFor("en", pageKey) }));

    // OpenGraph
    const og = {
      "og:type": "website",
      "og:site_name": "Independent Jennifer Lopez Tribute",
      "og:title": title,
      "og:description": description,
      "og:url": canonical,
      "og:image": OG_IMAGE,
      "og:locale": lang,
    };
    Object.entries(og).forEach(([property, content]) =>
      frag.appendChild(tag("meta", { property, content })),
    );

    // Twitter
    const tw = {
      "twitter:card": "summary_large_image",
      "twitter:title": title,
      "twitter:description": description,
      "twitter:image": OG_IMAGE,
    };
    Object.entries(tw).forEach(([name, content]) =>
      frag.appendChild(tag("meta", { name, content })),
    );

    // JSON-LD
    jsonLd.forEach((obj) => {
      const s = tag("script", { type: "application/ld+json" });
      s.textContent = JSON.stringify(obj);
      frag.appendChild(s);
    });

    document.head.appendChild(frag);
    window.scrollTo(0, 0);

    return () => clearManaged();
  }, [pageKey, title, description, lang, noindex, JSON.stringify(jsonLd)]);
}
